import axios from "axios";

// Resolve the backend URL.
// On Emergent, frontend and backend are always served on the same origin
// (the platform routes /api to the backend pod). Using window.location.origin
// makes the app domain-agnostic — works on preview, production, and any custom
// domain without rebuilding the bundle.
// Fall back to REACT_APP_BACKEND_URL only if there's no window (SSR/build).
const BACKEND_URL =
  typeof window !== "undefined"
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Public
export const fetchProperties = (params = {}) => api.get("/properties", { params }).then(r => r.data);
export const fetchProperty = (id) => api.get(`/properties/${id}`).then(r => r.data);
export const fetchTestimonials = () => api.get("/testimonials").then(r => r.data);
export const fetchStats = () => api.get("/stats").then(r => r.data);
export const submitBooking = (p) => api.post("/bookings", p).then(r => r.data);
export const submitOwnerInquiry = (p) => api.post("/owner-inquiries", p).then(r => r.data);
export const submitContact = (p) => api.post("/contact", p).then(r => r.data);

// Stripe
export const createCheckout = (p) => api.post("/checkout/session", p).then(r => r.data);
export const checkCheckoutStatus = (id) => api.get(`/checkout/status/${id}`).then(r => r.data);

// Pricing — length-of-stay discount ladder (single source of truth: backend/pricing.py)
export const fetchPricingTiers = () => api.get("/pricing/tiers").then(r => r.data.tiers);

// Auth
export const exchangeSession = (session_id) => api.post("/auth/session", { session_id }).then(r => r.data);
export const fetchMe = () => api.get("/auth/me").then(r => r.data);
export const logout = () => api.post("/auth/logout").then(r => r.data);

// Platform API (Sprint 1). All calls include X-Tenant-Id=wls; production would
// resolve this from the host/domain but for the admin surface we keep it
// explicit so the fail-closed resolver has something to work with.
const PLATFORM = `${BACKEND_URL}/api/platform/v1`;
export const platformApi = axios.create({
  baseURL: PLATFORM,
  withCredentials: true,
  headers: { "X-Tenant-Id": "wls" },
});
export const platformMeta = () => platformApi.get("/meta").then(r => r.data);
export const platformListAuditEvents = (params = {}) =>
  platformApi.get("/audit-events", { params }).then(r => r.data);
export const platformListFeatureFlags = () =>
  platformApi.get("/feature-flags").then(r => r.data);
export const platformUpdateFeatureFlag = (key, enabled) =>
  platformApi.patch(`/feature-flags/${encodeURIComponent(key)}`, { enabled }).then(r => r.data);

// Sprint 2 — Brand & Property administration.
export const platformGetBrand = () => platformApi.get("/brand").then(r => r.data);
export const platformPatchBrand = (patch) => platformApi.patch("/brand", patch).then(r => r.data);

export const platformListProperties = (params = {}) =>
  platformApi.get("/properties", { params }).then(r => r.data);
export const platformCreateProperty = (body) =>
  platformApi.post("/properties", body).then(r => r.data);
export const platformGetProperty = (id) =>
  platformApi.get(`/properties/${encodeURIComponent(id)}`).then(r => r.data);
export const platformPatchProperty = (id, patch) =>
  platformApi.patch(`/properties/${encodeURIComponent(id)}`, patch).then(r => r.data);
export const platformChangePropertyStatus = (id, status) =>
  platformApi.post(`/properties/${encodeURIComponent(id)}/status`, { status }).then(r => r.data);
export const platformListPropertyAudit = (id, params = {}) =>
  platformApi.get(`/properties/${encodeURIComponent(id)}/audit-events`, { params }).then(r => r.data);

// Sprint 4 — Public Concierge (shadow mode) + admin review surface.
// The public POST is intentionally NOT sent through `platformApi` because
// `platformApi` sets `withCredentials: true` and we don't want to leak
// admin cookies onto anonymous requests. We also hit the API on a plain
// axios instance so no interceptors add auth headers.
export const submitPublicConciergeMessage = ({ message, conversationId }) =>
  axios.post(
    `${API}/platform/v1/public/concierge/messages`,
    { message, ...(conversationId ? { conversationId } : {}) },
    { headers: { "X-Tenant-Id": "wls" }, withCredentials: false, timeout: 25000 },
  ).then(r => r.data);

export const platformListConciergeShadow = (params = {}) =>
  platformApi.get("/admin/concierge/shadow", { params }).then(r => r.data);
export const platformGetConciergeShadow = (id) =>
  platformApi.get(`/admin/concierge/shadow/${encodeURIComponent(id)}`).then(r => r.data);
export const platformReviewConciergeShadow = (id, body) =>
  platformApi.post(`/admin/concierge/shadow/${encodeURIComponent(id)}/review`, body).then(r => r.data);

// Public — read the widget-relevant flag snapshot without needing admin auth.
export const fetchPublicConciergeConfig = () =>
  axios.get(`${API}/platform/v1/public/concierge/config`, {
    headers: { "X-Tenant-Id": "wls" }, withCredentials: false, timeout: 8000,
  }).then(r => r.data);

// Sprint 5 — Knowledge Import MVP (admin only, feature-flag-gated)
export const platformSubmitKnowledgeImport = (body) =>
  platformApi.post("/knowledge-import-mvp", body).then(r => r.data);
export const platformGetKnowledgeImport = (jobId) =>
  platformApi.get(`/knowledge-import-mvp/${encodeURIComponent(jobId)}`).then(r => r.data);

// Admin
export const adminListProperties = () => api.get("/admin/properties").then(r => r.data);
export const adminCreateProperty = (p) => api.post("/admin/properties", p).then(r => r.data);
export const adminUpdateProperty = (id, p) => api.patch(`/admin/properties/${id}`, p).then(r => r.data);
export const adminDeleteProperty = (id) => api.delete(`/admin/properties/${id}`).then(r => r.data);
export const adminSetBlockedDates = (id, dates) => api.put(`/admin/properties/${id}/blocked-dates`, { dates }).then(r => r.data);
export const adminListBookings = () => api.get("/admin/bookings").then(r => r.data);
export const adminListOwnerInquiries = () => api.get("/admin/owner-inquiries").then(r => r.data);
export const adminListContact = () => api.get("/admin/contact-messages").then(r => r.data);
export const adminUpdateBooking = (id, payload) => api.patch(`/admin/bookings/${id}`, payload).then(r => r.data);
export const adminCalendar = () => api.get("/admin/calendar").then(r => r.data);
export const adminToggleBlockedDate = (pid, date) => api.put(`/admin/properties/${pid}/blocked-dates/toggle`, null, { params: { date } }).then(r => r.data);
export const adminListGuests = () => api.get("/admin/guests").then(r => r.data);
export const adminUpdateGuest = (email, payload) => api.put(`/admin/guests/${encodeURIComponent(email)}`, payload).then(r => r.data);
export const adminListPayments = () => api.get("/admin/payments").then(r => r.data);
